/*
 * Copyright (c) 2018 DV Bern AG, Switzerland
 *
 * Das vorliegende Dokument, einschliesslich aller seiner Teile, ist urheberrechtlich
 * geschuetzt. Jede Verwertung ist ohne Zustimmung der DV Bern AG unzulaessig. Dies gilt
 * insbesondere fuer Vervielfaeltigungen, die Einspeicherung und Verarbeitung in
 * elektronischer Form. Wird das Dokument einem Kunden im Rahmen der Projektarbeit zur
 * Ansicht uebergeben ist jede weitere Verteilung durch den Kunden an Dritte untersagt.
 *
 */

import {IHttpService, ILogService, IPromise, IQService, IRequestConfig, ITimeoutService} from 'angular';
import {TSAuthEvent} from '../../models/enums/TSAuthEvent';
import {TSRole} from '../../models/enums/TSRole';
import TSUser from '../../models/TSUser';
import TSAuthLoginElement from '../model/TSAuthLoginElement';
import HttpBuffer from './HttpBuffer';
import ICookiesService = angular.cookies.ICookiesService;
import IRootScopeService = angular.IRootScopeService;

export default class AuthServiceRS {

    private principal?: TSUser;

    static $inject = ['$http', 'CONSTANTS', '$q', '$timeout', '$cookies', 'base64', 'httpBuffer', '$rootScope', '$log'];

    /* @ngInject */
    constructor(private $http: IHttpService, private CONSTANTS: any, private $q: IQService, private $timeout: ITimeoutService,
                private $cookies: ICookiesService, private base64: any, private httpBuffer: HttpBuffer,
                private $rootScope: IRootScopeService, private $log: ILogService) {
    }

    public getPrincipal(): TSUser | undefined {
        return this.principal;
    }

    public getPrincipalRoles(): Array<TSRole> | undefined {
        if (this.principal) {
            return this.principal.roles;
        }
        return undefined;
    }

    public loginRequest(userCredentials?: TSAuthLoginElement): IPromise<TSUser | undefined> {
        if (userCredentials) {
            let objToPost = userCredentials.serialize({});
            return this.$http.post(this.CONSTANTS.REST_API + 'auth/login', objToPost)
                .then((response: any) => {

                    // try to reload buffered requests
                    this.httpBuffer.retryAll((config: IRequestConfig) => {
                        return config;
                    });
                    //ensure that there is ALWAYS a logout-event before the login-event by throwing it right before login
                    this.$rootScope.$broadcast(TSAuthEvent[TSAuthEvent.LOGOUT_SUCCESS], 'logged out before logging in in');
                    return this.$timeout((): any => { // Response cookies are not immediately accessible, so lets wait for a bit
                        try {
                            this.initWithCookie();

                            return this.principal;
                        } catch (e) {
                            return this.$q.reject();
                        }
                    }, 100);

                });

        }
        this.$log.error('passed no credentials, no login request sent');
        return this.$q.when(undefined);
    };

    public initWithCookie(): boolean {
        let authIdbase64 = this.$cookies.get('authId');
        if (authIdbase64) {
            try {
                // zuerst  uri decodieren und dann erst base 64
                const authData = JSON.parse(window.atob(decodeURIComponent(authIdbase64)));
                this.principal = new TSUser(authData.vorname, authData.name, '', authData.email, authData.roles);
                this.principal.id = authData.id;
                this.$timeout(() => {
                    this.$rootScope.$broadcast(TSAuthEvent[TSAuthEvent.LOGIN_SUCCESS], 'logged in');
                    let roles = (this.principal ? this.principal.roles : []);
                    this.$log.debug('Logged in as user ' + (this.principal ? this.principal.email : '') + ' with roles ' + roles + ' highest role is ' + this.getHighestRoleOfBenutzer(roles));
                }); //bei login muessen wir warten bis angular alle componenten erstellt hat bevor wir das event werfen

                return true;
            } catch (e) {
                console.log('cookie decoding failed');
            }
        }

        return false;
    };

    public logoutRequest() {
        return this.$http.post(this.CONSTANTS.REST_API + 'auth/logout', null).then((res: any) => {
            this.principal = undefined;
            this.$rootScope.$broadcast(TSAuthEvent[TSAuthEvent.LOGOUT_SUCCESS], 'logged out');
            return res;
        });
    };

    /**
     * Gibt die am privilegierteste Rolle des  Benutzers zurueck.
     */
    public getHighestRoleOfBenutzer(roles: Array<TSRole>): TSRole {
        let hasSuperAdmin: boolean = false;
        let hasAdmin: boolean = false;
        let hasFondssuisse: boolean = false;
        let hasAnlaufstelleVerwalter: boolean = false;
        let hasAnlaufstelleSachbearb: boolean = false;
        let hasSchaetzer: boolean = false;
        let hasGesuchsteller: boolean = false;
        let hasKantonVerwalter: boolean = false;
        let hasKantonInspektor: boolean = false;
        let hasUnassigned: boolean = false;
        for (let currRole of roles) {
            if (currRole === TSRole.SUPER_ADMIN) {
                hasSuperAdmin = true;
            } else if (currRole === TSRole.FONDSSUISSE_ADMIN) {
                hasAdmin = true;
            } else if (currRole === TSRole.FONDSSUISSE_SACHBEARBEITER) {
                hasFondssuisse = true;
            } else if (currRole === TSRole.ANLAUFSTELLE_SACHBEARBEITER) {
                hasAnlaufstelleSachbearb = true;
            } else if (currRole === TSRole.ANLAUFSTELLE_VERWALTER) {
                hasAnlaufstelleVerwalter = true;
            } else if (currRole === TSRole.ANLAUFSTELLE_SCHAETZER) {
                hasSchaetzer = true;
            } else if (currRole === TSRole.KANTON_VERWALTER) {
                hasKantonVerwalter = true;
            } else if (currRole === TSRole.KANTON_INSPEKTOR) {
                hasKantonInspektor = true;
            } else if (currRole === TSRole.BETROFFENER) {
                hasGesuchsteller = true;
            } else if (currRole === TSRole.UNASSIGNED_ROLE) {
                hasUnassigned = true;
            }

        }

        if (hasSuperAdmin) {
            return TSRole.SUPER_ADMIN;
        } else if (hasAdmin) {
            return TSRole.FONDSSUISSE_ADMIN;
        } else if (hasFondssuisse) {
            return TSRole.FONDSSUISSE_SACHBEARBEITER
        } else if (hasAnlaufstelleSachbearb) {
            return TSRole.ANLAUFSTELLE_SACHBEARBEITER
        } else if (hasAnlaufstelleVerwalter) {
            return TSRole.ANLAUFSTELLE_VERWALTER
        } else if (hasSchaetzer) {
            return TSRole.ANLAUFSTELLE_SCHAETZER
        } else if (hasKantonVerwalter) {
            return TSRole.KANTON_VERWALTER
        } else if (hasKantonInspektor) {
            return TSRole.KANTON_INSPEKTOR
        } else if (hasGesuchsteller) {
            return TSRole.BETROFFENER
        } else if (hasUnassigned) {
            return TSRole.UNASSIGNED_ROLE;
        }
        return TSRole.UNASSIGNED_ROLE;
    }

    /**
     * Gibt true zurueck, wenn der eingelogte Benutzer die gegebene Role hat. Fuer undefined Werte wird immer false zurueckgegeben.
     */
    public isRole(role: TSRole) {
        if (role && this.principal) {
            return this.principal.roles.filter(currRole => currRole === role).length !== 0;
        }
        return false;
    }

    /**
     * gibt true zurueck wenn der aktuelle Benutzer eine der uebergebenen Rollen innehat
     */
    public isOneOfRoles(roles: Array<TSRole>): boolean {
        if (roles !== undefined && roles !== null && this.principal) {
            for (let i = 0; i < roles.length; i++) {
                let role = roles[i];
                if (this.isRole(role)) {
                    return true;
                }
            }
        }
        return false;
    }

    public isLoggedId(): boolean {
        return this.getPrincipal() ? true : false;
    }
}



// WEBPACK FOOTER //
// ./src/app/authentication/service/AuthServiceRS.rest.ts